import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { MessageDto, MessageThreadDto, PostMessageInput } from "@tc/shared";
import { Button, Card, Icon, TextInput, type IconName } from "@/components/ui";
import { useAuthz } from "@/lib/auth/authz";
import { useFormatters } from "@/lib/i18n/format";
import { ApiError } from "@/features/trips/api";

/** Poll the thread on this cadence — matches the notification poll posture (§4.3); SSE later. */
const POLL_MS = 15_000;

/** The async operations a chat pane needs, supplied by the trip-thread or general-thread caller. */
export interface ChatSource {
  load: () => Promise<MessageThreadDto>;
  post: (body: PostMessageInput) => Promise<MessageThreadDto>;
  markRead: () => Promise<unknown>;
}

type Load =
  | { kind: "loading" }
  | { kind: "error" }
  | { kind: "ready"; thread: MessageThreadDto };

/** Optional pane header: a title, plus an optional leading icon, subtitle line and reference tag. */
export interface ChatPaneHeader {
  title: string;
  subtitle?: string;
  icon?: IconName;
  reference?: string | null;
}

/**
 * The reusable chat surface (TW-3453/3454): chat bubbles aligned by *side* (my audience vs the
 * other), an auto-scrolling pane polled while open, a compose box, and mark-read-on-open so the
 * unread count clears once the conversation is on screen. The data source (trip thread vs the
 * general club thread) is injected via {@link ChatSource}; `sourceKey` resets the pane when the
 * caller switches which thread is shown.
 */
export function ChatPane({
  source,
  sourceKey,
  header,
}: {
  source: ChatSource;
  sourceKey: string;
  header?: ChatPaneHeader;
}) {
  const { t } = useTranslation();
  const { isBackOffice } = useAuthz();
  const [state, setState] = useState<Load>({ kind: "loading" });
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const mounted = useRef(true);

  const load = useCallback(
    async (opts: { silent?: boolean } = {}) => {
      if (!opts.silent) setState({ kind: "loading" });
      try {
        const thread = await source.load();
        if (!mounted.current) return;
        setState({ kind: "ready", thread });
        if (thread.unreadCount > 0) void source.markRead().catch(() => undefined);
      } catch {
        if (mounted.current && !opts.silent) setState({ kind: "error" });
      }
    },
    [source],
  );

  useEffect(() => {
    mounted.current = true;
    setDraft("");
    void load();
    const id = window.setInterval(() => void load({ silent: true }), POLL_MS);
    return () => {
      mounted.current = false;
      window.clearInterval(id);
    };
    // `load` already closes over `source`; `sourceKey` resets the pane when the caller switches threads.
  }, [load, sourceKey]);

  const msgCount = state.kind === "ready" ? state.thread.messages.length : 0;
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgCount]);

  async function send() {
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    setSendError(false);
    try {
      const thread = await source.post({ body });
      if (!mounted.current) return;
      setDraft("");
      setState({ kind: "ready", thread });
    } catch (err) {
      if (mounted.current) setSendError(true);
      if (err instanceof ApiError) console.error(err.message);
    } finally {
      if (mounted.current) setSending(false);
    }
  }

  if (state.kind === "loading") return <div className="empty">{t("common.loading")}</div>;
  if (state.kind === "error")
    return (
      <Card>
        <div className="auth-alert" role="alert">
          <Icon name="alert" size={16} className="aa-ic" />
          <div className="aa-body">{t("messages.loadError")}</div>
        </div>
      </Card>
    );

  const { messages } = state.thread;

  return (
    <Card pad={false} className="msg-pane" style={{ overflow: "hidden" }}>
      <div className="msg-head">
        <div className="msg-head-main">
          {header?.icon ? <Icon name={header.icon} size={18} className="faint" /> : null}
          <div className="msg-head-text">
            <span className="msg-head-title">{header?.title ?? t("messages.title")}</span>
            {header?.subtitle ? <span className="msg-head-sub">{header.subtitle}</span> : null}
          </div>
        </div>
        {header?.reference ? <span className="t-id mono">{header.reference}</span> : null}
      </div>

      <div className="msg-scroll" ref={scrollRef}>
        {messages.length === 0 ? (
          <div className="sub" style={{ alignSelf: "center", margin: "auto 0" }}>{t("messages.empty")}</div>
        ) : (
          messages.map((m, i) => (
            <MessageRow
              key={m.id}
              message={m}
              mine={m.senderIsBackOffice === isBackOffice}
              showDay={i === 0 || dayOf(m.createdAt) !== dayOf(messages[i - 1]!.createdAt)}
            />
          ))
        )}
      </div>

      <div className="msg-compose">
        <TextInput
          className="msg-compose-field"
          value={draft}
          placeholder={t("messages.placeholder")}
          onChange={setDraft}
          onKeyDown={(e) => {
            if (e.key === "Enter") void send();
          }}
        />
        <Button variant="primary" icon="send" disabled={sending || draft.trim().length === 0} onClick={() => void send()}>
          {t("messages.send")}
        </Button>
      </div>
      {sendError && (
        <p className="sub" role="alert" style={{ fontSize: 11.5, padding: "0 14px 12px", color: "var(--danger)" }}>
          {t("messages.sendError")}
        </p>
      )}
    </Card>
  );
}

/** The ISO date portion (YYYY-MM-DD) for day grouping — coarse but stable across locales. */
function dayOf(iso: string): string {
  return iso.slice(0, 10);
}

function MessageRow({ message, mine, showDay }: { message: MessageDto; mine: boolean; showDay: boolean }) {
  const fmt = useFormatters();
  const { t } = useTranslation();
  const who = mine ? t("messages.you") : (message.senderName ?? t("messages.them"));
  const time = fmt.dateTime(message.createdAt, { hour: "2-digit", minute: "2-digit" });
  return (
    <>
      {showDay && <div className="msg-day">{fmt.date(message.createdAt)}</div>}
      <div className={`bubble ${mine ? "me" : "them"}`}>
        {message.body}
        <div className="b-meta">
          {who} · {time}
        </div>
      </div>
    </>
  );
}
